import React, { useEffect, useState } from 'react';
import { CheckoutEventNames } from '@paddle/paddle-js';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { apps, findApp } from '../data/apps';
import { loadPaddle, onPaddleEvent } from '../utils/paddle';

// 只处理 Paddle 生成的付款链接（?_ptxn=txn_…）。交易里已经有商品、邮箱和 custom_data（app、app_user_id），
// 订阅继承 custom_data，各 App 的 webhook 据此对应到账号，所以这里不需要登录
const TRANSACTION_ID = /^txn_[a-z0-9]+$/;

const Checkout: React.FC = () => {
  const transactionId = new URLSearchParams(window.location.search).get('_ptxn');

  return (
    <div className="App">
      <Header />
      <main>
        <div className="checkout-page">
          <div className="container checkout-container">
            {transactionId && TRANSACTION_ID.test(transactionId) ? <PaymentLink /> : <NoTransaction />}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

type State = 'loading' | 'open' | 'done' | 'error';

const PaymentLink: React.FC = () => {
  const [state, setState] = useState<State>('loading');
  const [appId, setAppId] = useState<string>();

  useEffect(() => {
    let active = true;
    const unsubscribe = onPaddleEvent((event) => {
      if (event.name === CheckoutEventNames.CHECKOUT_LOADED) setState('open');
      if (event.name === CheckoutEventNames.CHECKOUT_ERROR) setState('error');
      if (event.name === CheckoutEventNames.CHECKOUT_COMPLETED) {
        // custom_data.app 决定返回哪个 App 的网站
        const app = (event.data?.custom_data as { app?: unknown } | null | undefined)?.app;
        setAppId(typeof app === 'string' ? app : undefined);
        setState('done');
      }
    });
    loadPaddle()
      .then((paddle) => { if (active && !paddle) setState('error'); })
      .catch(() => { if (active) setState('error'); });
    return () => {
      active = false;
      unsubscribe();
    };
  }, []);

  const app = findApp(appId);

  return (
    <div className="checkout-card">
      <h1>Checkout</h1>
      {state === 'loading' && <p className="checkout-muted">Loading the secure checkout…</p>}
      {state === 'open' && <p className="checkout-muted">Complete the payment in the secure checkout window.</p>}
      {state === 'done' && (
        <>
          <p className="checkout-done">All done – thank you!</p>
          <p className="checkout-muted">Paddle has sent your receipt by email.</p>
          {app?.websiteUrl ? (
            <a href={app.websiteUrl} className="btn btn-primary">Return to {app.title}</a>
          ) : (
            <p className="checkout-muted">You can close this page.</p>
          )}
        </>
      )}
      {state === 'error' && (
        <p className="checkout-error">
          The checkout could not be loaded. Please reload the page, or email{' '}
          <a href="mailto:service@spiegeleistudio.com">service@spiegeleistudio.com</a>.
        </p>
      )}
      <p className="checkout-fine">
        Payments are processed by our online reseller Paddle.com, the Merchant of Record for all purchases on our websites.
      </p>
    </div>
  );
};

const NoTransaction: React.FC = () => {
  const billedApps = apps.filter((app) => app.webBilling && app.websiteUrl);

  return (
    <div className="checkout-card">
      <h1>Checkout</h1>
      <p className="checkout-muted">
        This page opens payment links that Paddle sends by email, for example to complete a purchase or
        update your payment method. The link in your email already contains everything needed.
      </p>
      <p className="checkout-muted">To subscribe to Premium, go to the website of the app:</p>
      <ul className="checkout-app-list">
        {billedApps.map((app) => (
          <li key={app.id}>
            <a href={app.websiteUrl} target="_blank" rel="noopener noreferrer">{app.title}</a>
            {' – '}
            <a href={app.webBilling!.pricingUrl} target="_blank" rel="noopener noreferrer">pricing</a>
          </li>
        ))}
      </ul>
      <p className="checkout-fine">
        Questions? Email <a href="mailto:service@spiegeleistudio.com">service@spiegeleistudio.com</a>.
      </p>
    </div>
  );
};

export default Checkout;
